import { useCallback, useEffect, useRef, useState } from "react";
import type {
	AssistantLogConversationMetaDto,
	AssistantLogMessageDto,
	AssistantLogTranscriptDto,
} from "@/types/ai-assistant-log";
import { fetchLogTranscript, TranscriptForbiddenError } from "./log.api";

/** Pesan per halaman transkrip (paginasi mundur). */
const PAGE_SIZE = 20;

export interface AssistantLogTranscriptState {
	meta: AssistantLogConversationMetaDto | null;
	user: AssistantLogTranscriptDto["user"] | null;
	/** Pesan terkumpul, urut lama → baru. */
	messages: AssistantLogMessageDto[];
	/** Kursor "muat pesan sebelumnya"; null = sudah di awal percakapan. */
	prevCursor: string | null;
	retentionDays: number;
	/** true bila ada isi yang disembunyikan (retensi atau toggle). */
	contentHidden: boolean;
	hideContent: boolean;
	setHideContent: (next: boolean) => void;
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
	notFound: boolean;
	/** true = admin belum punya izin transkrip (403). */
	forbidden: string | null;
	loadPrevious: () => void;
}

/**
 * Lapis 3: transkrip satu percakapan. Setiap pemuatan halaman pertama
 * tercatat di ActivityLog oleh server — karena itu "muat pesan sebelumnya"
 * memakai kursor dan tidak memuat ulang dari awal.
 *
 * Mengganti toggle "Sembunyikan isi" memuat ulang dari server (bukan hanya
 * menyembunyikan di browser) supaya isi yang tidak diminta memang tidak
 * pernah dikirim.
 */
export function useAssistantLogTranscript(
	conversationId: string,
): AssistantLogTranscriptState {
	const [hideContent, setHideContent] = useState(false);
	const [transcript, setTranscript] =
		useState<AssistantLogTranscriptDto | null>(null);
	const [messages, setMessages] = useState<AssistantLogMessageDto[]>([]);
	const [prevCursor, setPrevCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [notFound, setNotFound] = useState(false);
	const [forbidden, setForbidden] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		setError(null);
		setNotFound(false);
		setForbidden(null);
		fetchLogTranscript(
			conversationId,
			{ limit: PAGE_SIZE, hideContent: hideContent || undefined },
			controller.signal,
		)
			.then((result) => {
				setTranscript(result);
				setMessages(result.messages);
				setPrevCursor(result.prevCursor);
				setLoading(false);
			})
			.catch((err: unknown) => {
				if (err instanceof Error && err.name === "AbortError") return;
				setTranscript(null);
				setMessages([]);
				if (err instanceof TranscriptForbiddenError) setForbidden(err.message);
				else if (err instanceof Error && err.message.includes("(404)"))
					setNotFound(true);
				else
					setError(
						err instanceof Error ? err.message : "Gagal memuat transkrip",
					);
				setLoading(false);
			});
		return () => controller.abort();
	}, [conversationId, hideContent]);

	const loadPrevious = useCallback(async () => {
		if (!prevCursor) return;
		setLoadingMore(true);
		try {
			const result = await fetchLogTranscript(conversationId, {
				limit: PAGE_SIZE,
				cursor: prevCursor,
				hideContent: hideContent || undefined,
			});
			// Halaman mundur: pesan lebih lama ditaruh DI DEPAN yang sudah ada.
			setMessages((prev) => [...result.messages, ...prev]);
			setPrevCursor(result.prevCursor);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Gagal memuat pesan sebelumnya",
			);
		} finally {
			setLoadingMore(false);
		}
	}, [conversationId, prevCursor, hideContent]);

	return {
		meta: transcript?.conversation ?? null,
		user: transcript?.user ?? null,
		messages,
		prevCursor,
		retentionDays: transcript?.retentionDays ?? 0,
		contentHidden: transcript?.contentHidden ?? false,
		hideContent,
		setHideContent,
		loading,
		loadingMore,
		error,
		notFound,
		forbidden,
		loadPrevious,
	};
}
